import type { PitchFrameMessage } from '@tunasong/audio-dsp'
import type { PitchFrame } from '@tunasong/music-lib'
import type { IAudioContext, IAudioNode, IAudioWorkletNode } from 'standardized-audio-context'
import { AudioWorkletNode } from 'standardized-audio-context'
import invariant from 'tiny-invariant'
import { getPitchMicAudio } from '../lib/media-util.js'
import { loadPitchWorklet } from './pitch-worklet-loader.js'

/**
 * The Pitch Trainer's microphone.
 *
 * Subscription is deliberately the shape `TunerView` uses against an `AudioController` — an
 * `EventTarget` dispatching a `'pitch'` `CustomEvent<PitchFrame>` — so the React side reads the
 * same way. What it does *not* share is the stream: the engine's input channel is acquired
 * through `getMicAudio`, whose constraints set `autoGainControl: { exact: true }` because the
 * recorder wants it. §7 calls all three speech-tuned processors being off "not optional" for
 * this path, so sharing that stream would mean either breaking recording or breaking the
 * measurement. It takes its own, through `getPitchMicAudio`.
 *
 * The `tuner` DSP feature is untouched by any of this and keeps working exactly as it does.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §3.2, §7, §9
 */

export interface PitchCaptureOptions {
  context: IAudioContext
  deviceId?: string | null
  /** Injected in tests. Defaults to the real worklet node. */
  createNode?: (context: IAudioContext) => IAudioWorkletNode<IAudioContext>
  /** Injected in tests. Defaults to the shared module loader. */
  loadModule?: (context: IAudioContext) => Promise<void>
}

type CaptureState = 'idle' | 'starting' | 'running' | 'stopped'

export class PitchCapture extends EventTarget {
  private state: CaptureState = 'idle'
  private activeStream: MediaStream | null = null
  private source: IAudioNode<IAudioContext> | null = null
  private node: IAudioWorkletNode<IAudioContext> | null = null

  constructor(private options: PitchCaptureOptions) {
    super()
  }

  get active() {
    return this.state === 'running'
  }

  /**
   * The microphone this capture opened, as the browser names it.
   *
   * A wrong input device is otherwise invisible from inside the app: the stream is live, frames
   * arrive on time, and every one of them is silence. Chrome withholds the label until a
   * permission has been granted, so an empty string is a normal state and not an error.
   */
  get deviceLabel(): string | null {
    return this.activeStream?.getAudioTracks()[0]?.label ?? null
  }

  /**
   * The stream this capture opened, so a take can be recorded from the SAME microphone the tracker
   * is reading.
   *
   * Melody capture (`docs/superpowers/specs/2026-09-08-melody-plugin-design.md` §5) records the
   * performance alongside analysing it. A second `getUserMedia` would be a second microphone as far
   * as the browser is concerned — different constraints, different gain, a second recording
   * indicator — and the saved recording would then be a different performance from the one the
   * notes were derived from. Null until `start()` has resolved.
   */
  get stream(): MediaStream | null {
    return this.activeStream
  }

  /**
   * Open the microphone and start delivering frames.
   *
   * Every step re-checks that a `stop()` has not landed while an await was in flight. Both
   * awaits here are long by browser standards — a permission prompt and a module fetch — and a
   * user who starts and immediately navigates away must not be left holding a live microphone.
   */
  start = async (): Promise<void> => {
    if (this.state === 'starting' || this.state === 'running') {
      return
    }
    this.state = 'starting'
    const { context, deviceId = null, createNode, loadModule } = this.options

    const stream = await getPitchMicAudio(context, deviceId)
    if (this.state !== 'starting') {
      this.releaseStream(stream)
      return
    }
    this.activeStream = stream

    /**
     * From here the microphone is open, so a failure (the module fetch, the node) must release it before
     * rejecting: a live track keeps the recording indicator on and can make the next attempt fail (GH #2335).
     * `stop()` also leaves the capture startable again.
     */
    try {
      await (loadModule ?? loadPitchWorklet)(context)
      if (this.state !== 'starting') {
        return
      }

      this.source = context.createMediaStreamSource(stream)
      const node = (createNode ?? this.defaultCreateNode)(context)
      node.port.onmessage = this.handleMessage
      this.source.connect(node)
      this.node = node
      this.state = 'running'
    } catch (error) {
      this.stop()
      throw error
    }
  }

  /** Release everything. Safe to call at any point, including mid-`start`. */
  stop = (): void => {
    this.state = 'stopped'
    this.node?.disconnect()
    this.node = null
    this.source?.disconnect()
    this.source = null
    if (this.activeStream) {
      this.releaseStream(this.activeStream)
      this.activeStream = null
    }
  }

  addFrameListener = (callback: (ev: CustomEvent<PitchFrame>) => void): void => {
    super.addEventListener('pitch', callback as EventListener)
  }

  removeFrameListener = (callback: (ev: CustomEvent<PitchFrame>) => void): void => {
    super.removeEventListener('pitch', callback as EventListener)
  }

  private defaultCreateNode = (context: IAudioContext): IAudioWorkletNode<IAudioContext> => {
    invariant(AudioWorkletNode, 'AudioWorkletNode is not defined')
    return new AudioWorkletNode(context, 'pitch-worklet')
  }

  private handleMessage = (event: { data: PitchFrameMessage }) => {
    if (event.data.eventType !== 'pitchframe') {
      return
    }
    this.dispatchEvent(new CustomEvent<PitchFrame>('pitch', { detail: event.data.frame }))
  }

  private releaseStream = (stream: MediaStream) => {
    for (const track of stream.getTracks()) {
      track.stop()
    }
  }
}
